/**
 * @fileoverview Registre unique des sections de la page `/design-system`.
 *
 * Consommé à la fois par :
 *   - `DesignSystemLayout` — pour rendre la sidebar des ancres ;
 *   - `DesignSystemPage` — pour rendre les blocs de contenu.
 *
 * Un seul export garantit qu'on ne peut pas désynchroniser les deux (ordre,
 * slugs, présence de la section). Ajouter une section = ajouter une entrée
 * ici, exporter le composant dans `index.ts`, c'est tout.
 *
 * @see docs/design-system-catalog.md
 */

import type { ComponentType } from 'react';
import {
  DsSectionBadges,
  DsSectionButtons,
  DsSectionCards,
  DsSectionCharts,
  DsSectionColors,
  DsSectionDialogs,
  DsSectionDomainInputs,
  DsSectionEmptyStates,
  DsSectionForms,
  DsSectionIcons,
  DsSectionInputs,
  DsSectionLoading,
  DsSectionMenus,
  DsSectionLogos,
  DsSectionMotion,
  DsSectionPortal,
  DsSectionRadius,
  DsSectionSidebar,
  DsSectionSpacing,
  DsSectionTables,
  DsSectionToasts,
  DsSectionTypography,
  DsSectionVersion,
} from './index.ts';

export interface DsSection {
  /** Slug utilisé comme ancre HTML (`#colors`, `#buttons`…). */
  id: string;
  /** Titre affiché dans la sidebar et en tête de section. */
  title: string;
  /** Description courte, ton neutre — décrit CE QUE la section illustre. */
  description: string;
  /**
   * Composant vivant qui rend le contenu de la section. Absent tant que
   * la section est en placeholder « À livrer ».
   */
  component?: ComponentType;
}

/**
 * Table des sections DS. Ordre = ordre de découverte pédagogique
 * (fondations → primitives → composants composites → domain).
 */
export const DS_SECTIONS: readonly DsSection[] = [
  {
    id: 'version',
    title: 'Version',
    description:
      "Version courante, maturité, règles TSDoc et catalogue pour les IDE et les outils d'analyse du code.",
    component: DsSectionVersion,
  },
  {
    id: 'logos',
    title: 'Logos',
    description:
      "Assets brand Quipux — logo couleur, blanc, favicon, logos opérateurs mobile money et règles d'usage.",
    component: DsSectionLogos,
  },
  {
    id: 'colors',
    title: 'Colors',
    description: 'Palette Quipux (navy, orange, blue) et aliases sémantiques.',
    component: DsSectionColors,
  },
  {
    id: 'typography',
    title: 'Typography',
    description: 'Échelle Inter Variable + JetBrains Mono pour les identifiants techniques.',
    component: DsSectionTypography,
  },
  {
    id: 'spacing',
    title: 'Spacing',
    description: 'Grille 4 px, échelle standard, patterns récurrents.',
    component: DsSectionSpacing,
  },
  {
    id: 'radius',
    title: 'Radius',
    description: 'Coins arrondis — base 8 px, échelle none / sm / md / lg / xl / full.',
    component: DsSectionRadius,
  },
  {
    id: 'motion',
    title: 'Motion',
    description: 'Durations 100/200/400 ms, easings, respect prefers-reduced-motion.',
    component: DsSectionMotion,
  },
  {
    id: 'icons',
    title: 'Icons',
    description: 'Ensemble Lucide utilisé dans GNPC — actions, navigation, statuts, métier.',
    component: DsSectionIcons,
  },
  {
    id: 'portal',
    title: 'Portal',
    description: 'Composants métier du portail citoyen — plaque, montants, preuves, notices.',
    component: DsSectionPortal,
  },
  {
    id: 'buttons',
    title: 'Buttons',
    description: 'Variants primary / secondary / outline / ghost / destructive / link.',
    component: DsSectionButtons,
  },
  {
    id: 'inputs',
    title: 'Inputs',
    description: 'Input GNPC (44 px), FormField (label + tooltip + erreur), tailles et états.',
    component: DsSectionInputs,
  },
  {
    id: 'domain-inputs',
    title: 'Domain inputs',
    description: 'Inputs métier GNPC — PhoneNumberInput livré (téléphone CI, drapeau CI, E.164).',
    component: DsSectionDomainInputs,
  },
  {
    id: 'badges',
    title: 'Badges',
    description:
      'Badges génériques et StatusBadge (PaymentStatus, SgiStatus, RefundStatus, IntegrationStatus).',
    component: DsSectionBadges,
  },
  {
    id: 'cards',
    title: 'Cards',
    description: 'Card générique, MetricCard, PageHeader, SectionHeader et Timeline.',
    component: DsSectionCards,
  },
  {
    id: 'charts',
    title: 'Charts',
    description: 'Primitives Recharts GNPC — ChartContainer, ChartTooltip et séries tokenisées.',
    component: DsSectionCharts,
  },
  {
    id: 'empty-states',
    title: 'Empty states',
    description: 'États vides — patrons positif / calme / neutre selon le contexte.',
    component: DsSectionEmptyStates,
  },
  {
    id: 'dialogs',
    title: 'Dialogs',
    description: 'Dialog, AlertDialog, Sheet, Popover — les 4 patrons de contenu superposé.',
    component: DsSectionDialogs,
  },
  {
    id: 'menus',
    title: 'Menus',
    description:
      'DropdownMenu (actions, sous-menus, checkbox/radio items, raccourcis), Tabs et LanguageSwitcher.',
    component: DsSectionMenus,
  },
  {
    id: 'toasts',
    title: 'Toasts',
    description: 'Toaster Sonner + 4 variantes success / error / warning / info.',
    component: DsSectionToasts,
  },
  {
    id: 'loading',
    title: 'Loading states',
    description: 'Skeleton, Spinner et Progress — états de chargement bornés ou indéterminés.',
    component: DsSectionLoading,
  },
  {
    id: 'forms',
    title: 'Forms',
    description: 'react-hook-form + zod + FormField GNPC — démo invitation agent.',
    component: DsSectionForms,
  },
  {
    id: 'tables',
    title: 'Tables',
    description:
      'Table brute + DataTable @tanstack/react-table — tri, pagination cursor, filtres, states loading/empty.',
    component: DsSectionTables,
  },
  {
    id: 'sidebar',
    title: 'Sidebar',
    description:
      'Sidebar-07 shadcn colorisée navy Quipux — nav groupée par domaine, item actif orange, collapsible icon-only.',
    component: DsSectionSidebar,
  },
];
